<template>
  <div>
    <v-row v-if="alert.value" justify="center">
      <v-col xs="12" class="pa-2">
        <v-alert :value="alert.value" :type="alert.type">
          {{ $t(alert.message) }}
        </v-alert>
      </v-col>
    </v-row>
    <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
    <div class="d-md-none inventory-mobile-list mt-2 px-2 pb-4">
      <v-progress-linear v-if="loading" indeterminate color="red" class="mb-2" />

      <div
        v-for="(item, index) in parts"
        :key="item._id"
        class="inventory-mobile-card"
        :class="{
          'inventory-mobile-card--selected': index === activeRow,
          'inventory-mobile-card--moved': checkMovement(item),
        }"
        @click="activeRow = activeRow === index ? null : index"
      >
        <!-- Price badge top-right with inline edit -->
        <v-menu
          :model-value="mobileEditId === item._id"
          :close-on-content-click="false"
          offset="6"
          transition="scale-transition"
          @update:model-value="v => {
            if (v) { currentItem = item; editedPrice = item.price; mobileEditId = item._id; }
            else { mobileEditId = null; }
          }"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              class="inventory-mobile-card__price"
              variant="outlined"
              size="x-small"
              @click.stop
            >
              {{ item.price }} {{ item.currency }}
              <v-icon size="12" class="ml-1">mdi-pencil</v-icon>
            </v-btn>
          </template>
          <v-card width="220" elevation="8">
            <v-card-text class="pb-0 mb-0">
              <v-text-field
                :model-value="editedPrice"
                :disabled="$store.getters.role === 'User' || $store.getters.role === 'EbayOperator'"
                color="red-darken-1"
                type="number"
                step="0.01"
                min="0"
                max="10000"
                density="compact"
                hide-details
                variant="outlined"
                :data-ok="editedPrice"
                oninput="if(this.value!==''&&Number(this.value)>10000){this.value=this.getAttribute('data-ok')??'';}else{this.setAttribute('data-ok',this.value);}"
                @update:model-value="editedPrice = keepPriceIfValid($event, editedPrice)"
              />
            </v-card-text>
            <v-card-actions class="mt-0 pt-0">
              <v-spacer />
              <v-btn icon variant="text" @click.stop="mobileEditId = null; cancelPrice()">
                <v-icon>mdi-close</v-icon>
              </v-btn>
              <v-btn icon variant="text" color="primary" @click.stop="mobileEditId = null; savePriceDialog()">
                <v-icon>mdi-check</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>

        <!-- Main row: checkbox + image + info -->
        <div class="inventory-mobile-card__main">
          <v-checkbox
            :model-value="selected.includes(item)"
            :disabled="item.status === 4 || item.status === 5"
            color="primary"
            hide-details
            density="compact"
            class="flex-shrink-0 mt-1"
            @change.stop="toggleSelection(item)"
            @click.stop
          />

          <div class="inventory-mobile-card__media">
            <button
              type="button"
              class="inventory-mobile-card__image-btn"
              @click.stop="showImages(item.images)"
            >
              <img
                :src="
                  item.images && item.images.length > 0 && item.images[0].thumb
                    ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.images[0].thumb}`
                    : `https://portal.lekotech.com/logo_small.png`
                "
                class="inventory-mobile-card__img"
              />
            </button>
            <button
              type="button"
              class="inventory-mobile-card__qr-btn"
              @click.stop="openQrDialog({ id: item.old_id, type: 'inventory', car: item.car_id, printed: item.printed })"
            >
              {{ item.old_id ? item.old_id.slice(-6) : '' }}
            </button>
          </div>

          <div class="inventory-mobile-card__info">
            <div class="inventory-mobile-card__info-top">
              <v-chip
                v-if="
                  ($store.getters.role === 'LekoAdmin' ||
                    $store.getters.role === 'SuperAdmin') &&
                  item.institution_id
                "
                size="x-small"
                class="inventory-mobile-card__institution mb-1"
              >
                {{ item.institution_id.title }}
              </v-chip>
              <!-- Category -->
              <div
                v-if="item.infoCar?.description?.descriptionId?.category"
                class="text-caption text-grey-darken-1"
              >
                {{ item.infoCar.description.descriptionId.category[$store.getters.language] }}
              </div>
              <div v-else> - </div>

            
            </div>
  <!-- Title + position -->
              <div
                v-if="item.infoCar?.description?.descriptionId?.title"
                class="font-weight-bold text-primary text-body-2"
              >
                {{ item.infoCar.description.descriptionId.title[$store.getters.language] }}
                <span
                  v-if="item.infoCar.description.positionCode"
                  class="text-caption text-grey font-weight-regular"
                >
                  {{ getPosition(item.infoCar.description.positionCode) }}
                </span>
              </div>
              <eco-score-badge
                v-if="ecoScoreFor(item)"
                class="mt-1"
                compact
                :score="ecoScoreFor(item).score"
                :second-hand-kg="ecoScoreFor(item).secondHandCO2"
                :new-kg="ecoScoreFor(item).newCO2"
              />
            <!-- OEM + manufacturer code -->
            <div class="text-caption mt-1">
              <span v-if="item.infoCar?.oemCode">
                OEM: <strong>{{ item.infoCar.oemCode }}</strong>
              </span>
              <span v-if="item.manufacturer_code" :class="item.infoCar?.oemCode ? 'ml-2' : ''">
                Cod: <strong>{{ item.manufacturer_code }}</strong>
              </span>
            </div>


            <!-- Lotto + shelf (same info as desktop "Lotto" column) -->
            <div
              v-if="item.shipment_id || item.shelf_data"
              class="inventory-mobile-card__location text-caption mt-1"
            >
              <div
                v-if="item.shipment_id"
                class="inventory-mobile-card__location-row"
                :class="{ 'text-primary': item.car_id }"
              >
                <v-icon size="12" class="flex-shrink-0 mt-px">mdi-package-variant</v-icon>
                <span>{{ item.shipment_id.name }}</span>
              </div>
              <div
                v-if="item.shelf_data?.shelf"
                class="inventory-mobile-card__location-row text-grey-darken-2"
              >
                <v-icon size="12" class="flex-shrink-0 mt-px">mdi-warehouse</v-icon>
                <span>
                 <span v-if="item.shelf_data.repo" class="font-weight-bold">
                
                    {{ item.shelf_data.repo }}
                  </span>
                  {{ formatMobileShelfPosition(item) }}
                 
                </span>
              </div>
            </div>

            <!-- Date -->
            <div class="text-caption text-grey-darken-1">
              {{ formatDate(item.date_created) }}
            </div>


            <!-- eBay inline (only when institution has eBay enabled) -->
            <div
              v-if="
                $store.getters.institutionExportEbayLeko ||
                $store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'SuperAdmin'
              "
              class="mt-1 d-flex align-center ga-1"
            >
              <!-- Not listed yet -->
              <v-btn
                v-if="!item.eBayListings || Object.values(item.eBayListings).every(l => !l || l.length === 0)"
                size="x-small" variant="outlined" color="success"
                :disabled="
                  !$store.getters.institutionExportEbayLeko ||
                  (($store.getters.role === 'LekoAdmin' || $store.getters.role === 'SuperAdmin') &&
                    !item.institution_id?.export_ebay_leko)
                "
                @click.stop="openEbayLink(item)"
              >
                <v-icon start size="14">mdi-tag-plus-outline</v-icon>
                eBay
              </v-btn>

              <!-- Already listed -->
              <template v-if="item.eBayStatus">
                <v-chip
                  size="x-small" color="success" variant="flat"
                  class="pointer"
                  @click.stop="openEbayLink(item)"
                >
                  <v-icon start size="12">mdi-tag-check-outline</v-icon>
                  eBay
                </v-chip>
                <v-btn
                   variant="outlined" size="x-small" color="error"
                  :disabled="!$store.getters.institutionExportEbayLeko"
                  @click.stop="openDeleteEbay(item)"
                >
                  <v-icon size="16" color="error">mdi-delete-outline</v-icon>
                </v-btn>
              </template>

              <!-- kTypes (next to eBay) -->
              <v-chip
                v-if="item.infoCar && item.infoCar.kTypes && item.infoCar.kTypes.length > 0"
                size="x-small"
                color="primary"
                variant="tonal"
                class="pointer"
                @click.stop="checkKtypes(item)"
              >
                <v-icon start size="12">mdi-check-circle-outline</v-icon>
                {{ $t('parts.table.kTypes') }} ({{ item.infoCar.kTypes.length }})
              </v-chip>
              <v-chip
                v-else
                size="x-small"
                color="grey"
                variant="tonal"
              >
                <v-icon start size="12">mdi-cancel</v-icon>
                {{ $t('parts.table.kTypes') }}
              </v-chip>
            </div>
          </div>
        </div>

        <!-- Actions row -->
        <div class="inventory-mobile-card__actions">
          <!-- Status chip -->
          <v-chip
            v-if="checkMovement(item)"
            size="small" color="black" variant="tonal" class="mr-1 text-caption font-weight-medium text-black"
            @click.stop="movement(item)"
          >{{ $t('parts.status.returned') }}</v-chip>
          <v-chip
            v-else-if="item.status === 1"
            size="small" color="success" variant="tonal" class="mr-1 text-caption font-weight-medium text-black"
            @click.stop="movement(item)"
          >{{ inventoryEnteredLabel(item) }}</v-chip>
          <v-chip
            v-else-if="item.status === 2"
            size="small" color="error" variant="tonal" class="mr-1 text-caption font-weight-medium text-black"
            @click.stop="movement(item)"
          >{{ $t('parts.status.returned') }}</v-chip>
          <v-chip
            v-else-if="item.status === 2.1"
            size="small" color="error" variant="tonal" class="mr-1 text-caption font-weight-medium text-black"
            @click.stop="movement(item)"
          >{{ $t('parts.status.returned_shop') }}</v-chip>
          <v-chip
            v-else-if="item.status === 3"
            size="small" color="info" variant="tonal" class="mr-1 text-caption font-weight-medium text-black"
            @click.stop="movement(item)"
          >{{ $t('parts.status.re_entered') }}</v-chip>
          <v-chip
            v-else-if="item.status === 4"
            size="small" color="info" variant="tonal" class="mr-1 text-caption font-weight-medium text-black"
            @click.stop="movement(item)"
          >{{ $t('parts.status.found') }}</v-chip>

          <v-spacer />

          <v-btn icon variant="text" size="small" @click.stop="details(item)">
            <v-icon size="20" color="grey-darken-2">mdi-view-list</v-icon>
          </v-btn>
          <v-btn icon variant="text" size="small" @click.stop="actions(item)">
            <v-icon size="20" color="grey-darken-2">mdi-compare-horizontal</v-icon>
          </v-btn>
          <v-btn icon variant="text" size="small" @click.stop="edit(item)">
            <v-icon size="20" color="grey-darken-2">mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="item.car_id"
            icon variant="text" size="small"
            @click.stop="viewCar(item.car_id)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-car</v-icon>
          </v-btn>
          <v-btn v-else icon variant="text" size="small" disabled>
            <v-icon size="20" color="grey-darken-2">mdi-car-off</v-icon>
          </v-btn>
          <v-btn icon variant="text" size="small" @click.stop="share(item)">
            <v-icon size="20" color="grey-darken-2">mdi-share-variant-outline</v-icon>
          </v-btn>
          <v-btn
            v-if="item.refusal_reason"
            icon variant="text" size="small"
            @click.stop="showRefusalReason(item.refusal_reason)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-information</v-icon>
          </v-btn>
          <v-btn
            v-if="$store.getters.role === 'LekoAdmin' || $store.getters.role === 'SuperAdmin'"
            icon variant="text" size="small"
            @click.stop="deletePart(item._id, item.kromeda_id)"
          >
            <v-icon size="20" color="error">mdi-delete</v-icon>
          </v-btn>

        </div>
      </div>

      <v-alert v-if="!loading && parts.length < 1" type="info" variant="tonal" class="mt-2">
        No data available.
      </v-alert>
    </div>
    <!-- ===================================================================== -->
    <div ref="tableHost" class="inventory-table-host pb-2">
      <v-data-table-server
        v-model="selected"
        v-model:options="pagination"
        :headers="
          $store.getters.role === 'LekoAdmin' ||
          $store.getters.role === 'SuperAdmin'
            ? headersAdmin
            : headers
        "
        :items="parts"
        :items-length="totalItems"
        :items-per-page="pagination.itemsPerPage"
        :items-per-page-options="[
          { value: 5, title: '5' },
          { value: 10, title: '10' },
          { value: 25, title: '25' },
          { value: 50, title: '50' },
        ]"
        items-per-page-text="Rows per page"
        item-value="_id"
        return-object
        show-select
        :loading="loading"
        class="elevation-1 pb-12 nowrap-headers"
        @update:options="updatePagination"
      >
        <template #item="{ item, index }">
          <tr
            :class="{ tableRowColorSelected: index === activeRow }"
            :style="checkMovement(item) ? 'background-color: #afabab;' : ''"
            class="tableRowColor"
            @click="activeRow = activeRow === index ? null : index"
          >
            <!-- SELECTION -->
            <td>
              <v-row align="center" justify="center">
                <v-checkbox
                  :model-value="selected.includes(item)"
                  :disabled="item.status === 4 || item.status === 5"
                  color="primary"
                  hide-details
                  dense
                  @change="toggleSelection(item)"
                />
              </v-row>
            </td>

            <!-- QR Code -->
            <td class="ma-1 pt-1 pb-1">
              <v-row
                class="pointer g-0"
                align="center"
                justify="center"
                @click="
                  openQrDialog({
                    id: item.old_id,
                    type: 'inventory',
                    car: item.car_id,
                    printed: item.printed,
                  })
                "
              >
                <qr-code :size="60" :text="item.old_id" />
                <h4>{{ item.old_id.slice(-6) }}</h4>
              </v-row>
            </td>

            <!-- ACTIONS -->

            <td class="text-center">
              <v-row no-gutters class="d-flex flex-nowrap">
                <!-- <v-btn
                v-if="
                  !item?.car_id &&
                  (!item?.infoCar?.metadata ||
                    item.infoCar.metadata.length === 0)
                    && !customApplicability
                "
                icon
                variant="text"
                class="ma-0"
                 @click="details(item)"
                :disabled="$store.getters.role !== 'LekoAdmin' &&
                  $store.getters.role !== 'SuperAdmin' && $store.getters.role !== 'InstitutionAdmin'"
              >
                <v-icon size="large" color="grey-darken-2"
                  >mdi-playlist-remove</v-icon
                >
              </v-btn> -->
                <v-btn icon variant="text" class="ma-0" @click="details(item)">
                  <v-icon color="grey-darken-2">mdi-view-list</v-icon>
                </v-btn>
                <v-btn icon variant="text" class="ma-0" @click="actions(item)">
                  <v-icon color="grey-darken-2">mdi-compare-horizontal</v-icon>
                </v-btn>
                <v-btn icon variant="text" class="ma-0" @click="edit(item)">
                  <v-icon color="grey-darken-2">mdi-pencil</v-icon>
                </v-btn>
              </v-row>
              <v-row no-gutters class="d-flex flex-nowrap">
                <v-btn
                  v-if="item.car_id"
                  icon
                  variant="text"
                  class="ma-0"
                  @click="viewCar(item.car_id)"
                >
                  <v-icon color="grey-darken-2">mdi-car</v-icon>
                </v-btn>
                <v-btn v-else icon variant="text" disabled class="ma-0">
                  <v-icon color="grey-darken-2">mdi-car-off</v-icon>
                </v-btn>
                <v-btn icon variant="text" class="ma-0" @click="share(item)">
                  <v-icon color="grey-darken-2"
                    >mdi-share-variant-outline</v-icon
                  >
                </v-btn>
                <v-btn
                  v-if="item.refusal_reason"
                  icon
                  variant="text"
                  class="ma-0"
                  @click="showRefusalReason(item.refusal_reason)"
                >
                  <v-icon color="grey-darken-2">mdi-information</v-icon>
                </v-btn>
                <v-btn
                  v-if="
                    $store.getters.role === 'LekoAdmin' ||
                    $store.getters.role === 'SuperAdmin'
                  "
                  icon
                  variant="text"
                  class="ma-0"
                  @click="deletePart(item._id, item.kromeda_id)"
                >
                  <v-icon color="grey-darken-2">mdi-delete</v-icon>
                </v-btn>
              </v-row>
            </td>
            <!---------------------------------------SHIPMENT---------------------------------------->
            <td class="pa-2" style="min-width: 170px">
              <span
                v-if="item.shipment_id"
                :style="item.car_id ? 'color:#1174d0' : ''"
              >
                {{ item.shipment_id.name }}<br />
              </span>
              <span v-if="item.shelf_data">
                {{ item.shelf_data.shelf.room_name }} -
                {{ item.shelf_data.shelf.location_code }}:
                {{ item.shelf_data.shelf.x }}, {{ item.shelf_data.shelf.y
                }}<br />
                <span class="caption">Mag: </span>
                <span class="font-weight-bold caption">{{
                  item.shelf_data.repo
                }}</span>
              </span>
              <span v-else> - </span>
            </td>
            <!--------------------------------------LEKO GROUP--------------------------------------->
            <td>
              <v-row column>
                <v-col>
                  <span
                    v-if="
                      item.infoCar &&
                      item.infoCar.description &&
                      item.infoCar.description.descriptionId &&
                      item.infoCar.description.descriptionId.group
                    "
                    class="text-xs font-weight-bold"
                  >
                    <!-- eslint-disable-next-line max-len -->
                    {{
                      item.infoCar.description.descriptionId.group[
                        $store.getters.language
                      ]
                    }}
                  </span>
                </v-col>
              </v-row>
            </td>
            <!-----------------------------------------GROUP----------------------------------------->
            <td>
              <v-row column>
                <v-col>
                  <span
                    v-if="
                      item.infoCar &&
                      item.infoCar.description &&
                      item.infoCar.description.descriptionId &&
                      item.infoCar.description.descriptionId.category
                    "
                    class="text-xs font-weight-bold"
                  >
                    <!-- eslint-disable-next-line max-len -->
                    {{
                      item.infoCar.description.descriptionId.category[
                        $store.getters.language
                      ]
                    }}
                  </span>
                </v-col>
              </v-row>
            </td>
            <!-----------------------------------------TITLE----------------------------------------->
            <td class="text-xs" style="min-width: 200px">
              <v-row column>
                <v-col v-if="item.infoCar && item.infoCar.description">
                  <span
                    v-if="
                      item.infoCar.description.descriptionId &&
                      item.infoCar.description.descriptionId.title
                    "
                    class="text-xs font-weight-bold"
                  >
                    {{
                      item.infoCar.description.descriptionId.title[
                        $store.getters.language
                      ]
                    }}
                  </span>
                  <template
                    v-for="(alert, ind) in positionAlertItems(item)"
                    :key="`position-alert-${item._id}-${ind}`"
                  >
                    <v-tooltip
                      v-if="item.alerts && item.alerts.status === true"
                      top
                    >
                      <template #activator="{ props }">
                        <v-icon
                          v-bind="props"
                          :color="getAlertColor(alert.type)"
                          size="18"
                          class="ml-1 cursor-pointer"
                        >
                          {{ getAlertIcon(alert.type) }}
                        </v-icon>
                      </template>

                      <span>{{ $t(alert.reason) }}</span>
                    </v-tooltip>
                  </template>
                  <span
                    v-if="item.infoCar.description.positionCode"
                    class="text-xs"
                  >
                    &nbsp;
                    {{ getPosition(item.infoCar.description.positionCode) }}
                  </span>
                </v-col>
              </v-row>
            </td>
            <!-----------------------------------MANUFACTURER CODE----------------------------------->
            <td class="text-xs">
              <!--            {{ item.infoCar.oemCode }}-->
              {{ item.manufacturer_code }}
            </td>
            <!--------------------------------------INSTITUTION-------------------------------------->
            <td
              v-if="
                ($store.getters.role === 'LekoAdmin' ||
                  $store.getters.role === 'SuperAdmin') &&
                item.institution_id
              "
              class="text-xs"
            >
              <v-chip>
                {{ item.institution_id.title }}
              </v-chip>
            </td>
            <td
              v-else-if="
                ($store.getters.role === 'LekoAdmin' ||
                  $store.getters.role === 'SuperAdmin') &&
                !item.institution_id
              "
            />
            <!----------------------------------------IMAGES----------------------------------------->
            <td>
              <v-row align-center justify-center>
                <img
                  :src="
                    item.images &&
                    item.images.length > 0 &&
                    item.images[0].thumb
                      ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.images[0].thumb}`
                      : `https://portal.lekotech.com/logo_small.png`
                  "
                  class="pointer"
                  height="75"
                  @click="showImages(item.images)"
                />
              </v-row>
            </td>
            <!-------------------------------------CONDITION-------------------------------------->
            <td class="text-xs">
              <span v-if="item.isNewPart">
                <v-icon>mdi-check-circle-outline</v-icon>
              </span>
            </td>
            <!-------------------------------------INFOCAR CODE-------------------------------------->
            <td class="text-xs">
              {{ item.infoCar.oemCode }}
            </td>
            <!-----------------------------------------PRICE----------------------------------------->
            <td class="text-xs" style="min-width: 130px">
              <v-row no-gutters class="d-flex flex-nowrap align-center">
                <div class="d-flex flex-nowrap mt-1">
                  {{ item.price }} {{ item.currency }}
                </div>
                <!-- <v-btn
                v-if="item.infoCar && item.infoCar.oemCode"
                icon
                variant="outlined"
                size="small"
                @click="openPriceAverages(item.infoCar.oemCode)"
              >
                <v-icon color="grey-darken-2" size="small"
                  >mdi-currency-eur</v-icon
                ></v-btn
              > -->
                <v-menu
                  v-model="activeEditId"
                  :model-value="activeEditId === item._id"
                  :close-on-content-click="false"
                  activator="parent"
                  offset="6"
                  transition="scale-transition"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon
                      variant="text"
                      @click.stop="toggleMenu(item)"
                    >
                      <v-icon color="grey-darken-2">mdi-pencil</v-icon>
                    </v-btn>
                  </template>

                  <v-card width="220" elevation="8">
                    <v-card-text class="pb-0 mb-0">
                      <v-text-field
                        :model-value="editedPrice"
                        :disabled="
                          $store.getters.role === 'User' ||
                          $store.getters.role === 'EbayOperator'
                        "
                        color="red-darken-1"
                        type="number"
                        step="0.01"
                        min="0"
                        max="10000"
                        density="compact"
                        hide-details
                        variant="outlined"
                        :data-ok="editedPrice"
                        oninput="if(this.value!==''&&Number(this.value)>10000){this.value=this.getAttribute('data-ok')??'';}else{this.setAttribute('data-ok',this.value);}"
                        @update:model-value="editedPrice = keepPriceIfValid($event, editedPrice)"
                      />
                    </v-card-text>
                    <v-card-actions class="mt-0 pt-0">
                      <v-spacer></v-spacer>
                      <v-btn icon variant="text" @click="cancelPrice">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        variant="text"
                        color="primary"
                        @click="savePriceDialog"
                      >
                        <v-icon>mdi-check</v-icon>
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </v-row>
            </td>
            <td class="text-xs">
              <eco-score-badge
                v-if="ecoScoreFor(item)"
                compact
                :score="ecoScoreFor(item).score"
                :second-hand-kg="ecoScoreFor(item).secondHandCO2"
                :new-kg="ecoScoreFor(item).newCO2"
              />
            </td>

            <!--------------------------------------PRA NUMBER--------------------------------------->
            <td class="text-xs">
              {{ item.pra }}
            </td>
            <!-----------------------------------------NOTES----------------------------------------->
            <td class="text-xs">
              <v-row
                align="center"
                justify="center"
                class="pointer flex-column"
                style="min-width: 125px !important"
                @click="showNotes(item.notes)"
              >
                <span v-if="item.notes && item.notes.length > 40">
                  {{ item.notes.substring(0, 40) }}... <v-icon>mdi-eye</v-icon
                  >{{ item.notes ? "(" + item.notes.length + ")" : "" }}
                </span>
                <span v-else
                  >{{ item.notes
                  }}{{ item.notes ? "(" + item.notes.length + ")" : "" }}</span
                >
                <v-chip
                  v-if="showEngineCodeNotesMatch(item)"
                  size="x-small"
                  color="amber-darken-3"
                  class="mt-1"
                  variant="flat"
                >
                  {{ $t("parts.inventory.engine_code_match_notes") }}
                </v-chip>
              </v-row>
            </td>
            <!-----------------------------------------BRAND----------------------------------------->
            <td class="text-xs">
              {{
                item.brand && item.brand.toLowerCase().includes("error")
                  ? ""
                  : item.brand
              }}
            </td>
            <!---------------------------------------EBAY Category--------------------------------------->
            <td
              v-if="
                $store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'SuperAdmin' ||
                $store.getters.institutionExportEbayLeko
              "
              class="text-xs"
            >
              {{ item.ebayCode }}
              <br />
              <small
                v-if="
                  item.infoCar &&
                  item.infoCar.description &&
                  item.infoCar.description.descriptionId &&
                  item.infoCar.description.descriptionId.ebayCategory
                "
              >
                <strong> {{ $t("parts.inventory.ebay_category") }}</strong>
                {{
                  item.infoCar.description.descriptionId.ebayCategory
                    ? item.infoCar.description.descriptionId.ebayCategory[
                        $store.getters.language
                      ]
                    : ""
                }}
              </small>
            </td>
            <!---------------------------------------EBAY Title--------------------------------------->
            <td
              v-if="
                $store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'SuperAdmin' ||
                $store.getters.institutionExportEbayLeko
              "
              class="text-xs"
            >
              <br />
              <small v-if="item && item.eBayStatus == true" class="d-flex">
                {{ generateTitle(item) }}
                <v-btn
                  :disabled="
                    !$store.getters.institutionExportEbayLeko ||
                    (($store.getters.role == 'LekoAdmin' ||
                      $store.getters.role == 'SuperAdmin') &&
                      !item.institution_id.export_ebay_leko)
                  "
                  class="pa-0 ma-0"
                  icon
                  variant="text"
                  @click="openEditTitleEbay(item)"
                >
                  <v-icon class="pa-0 ma-0" color="grey-darken-2">
                    mdi-pencil
                  </v-icon>
                </v-btn>
              </small>
            </td>
            <!---------------------------------------Google--------------------------------------->
            <td
              v-if="
                $store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'SuperAdmin' ||
                $store.getters.institutionExportGoogleLeko
              "
              class="text-xs"
            >
              <span class="d-flex text-center justify-center">
                <v-icon
                  v-if="item.googleMerchantStatus == true"
                  class="ma-1"
                  color="#5a5a5a"
                  >mdi-check-circle-outline</v-icon
                >
                <v-btn
                  v-if="item.googleMerchantStatus == false"
                  icon
                  variant="text"
                  class="ma-1"
                  :disabled="
                    !$store.getters.institutionExportGoogleLeko ||
                    (($store.getters.role == 'LekoAdmin' ||
                      $store.getters.role == 'SuperAdmin') &&
                      !item.institution_id.export_google_merchant_leko)
                  "
                  @click="openAddGoogle(item)"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
                <!-- <v-btn
                v-if="item.googleMerchantStatus == true"
                class="ml-0 pl-0"
                icon
                variant="text"
                
              > -->
                <v-icon
                  v-if="item && item.googleMerchantStatus == true"
                  :disabled="
                    !$store.getters.institutionExportGoogleLeko ||
                    (($store.getters.role == 'LekoAdmin' ||
                      $store.getters.role == 'SuperAdmin') &&
                      !item.institution_id.export_google_merchant_leko)
                  "
                  icon
                  variant="text"
                  class="ma-1"
                  color="#5a5a5a"
                  @click="openDeleteGoogle(item)"
                  >mdi-delete</v-icon
                >
                <!-- </v-btn> -->
              </span>
              <!-- <span
              style="color: #1174d0; text-decoration: underline"
              class="text-center"
            >
              <a
                v-if="item && item.googleMerchantStatus == true"
                role="button"
                class="pt-1 text-center text-truncate"
                @click="openGoogleLink(item)"
              >
                {{ $t("parts.inventory.see_google") }}</a
              >
            </span> -->
            </td>
            <!-------------------------------------EBAY ACTIONS------------------------------------->
            <td
              v-if="
                $store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'SuperAdmin' ||
                $store.getters.institutionExportEbayLeko
              "
              class="text-center"
            >
              <!-- <v-icon v-if="item.ebayCode">edit</v-icon> -->
              <span class="d-flex text-center justify-center">
                <v-btn
                  v-if="
                    item &&
                    (!item.eBayListings ||
                      Object.values(item.eBayListings).every(
                        (list) => !list || list.length === 0,
                      ))
                  "
                  icon
                  variant="text"
                  :disabled="
                    !$store.getters.institutionExportEbayLeko ||
                    (($store.getters.role == 'LekoAdmin' ||
                      $store.getters.role == 'SuperAdmin') &&
                      !item.institution_id.export_ebay_leko)
                  "
                  @click="openEbayLink(item)"
                >
                  <v-icon color="#5a5a5a">mdi-plus</v-icon>
                </v-btn>
                <!-- <v-btn
                  icon 
                  variant="text"
                  class="pa-0 ma-0"
                  > -->
                <v-icon
                  v-if="item && item.eBayStatus == true"
                  color="#5a5a5a"
                  class="ma-1"
                  >mdi-check-circle-outline</v-icon
                >
                <!-- </v-btn> -->

                <!-- <v-btn 
                  v-if="item && item.eBayStatus == true"
                  icon 
                  variant="text"
                  class="pa-0 ma-0"
                  :disabled="!$store.getters.institutionExportEbayLeko"
                  @click="openDeleteEbay(item)"
                >  -->
                <v-icon
                  v-if="item && item.eBayStatus == true"
                  icon
                  variant="text"
                  class="ma-1"
                  :disabled="!$store.getters.institutionExportEbayLeko"
                  color="#5a5a5a"
                  @click="openDeleteEbay(item)"
                  >mdi-delete</v-icon
                >
                <!-- </v-btn> -->
              </span>
              <span
                style="color: #1174d0; text-decoration: underline"
                class="text-center"
              >
                <a
                  v-if="item && item.eBayStatus == true"
                  role="button"
                  class="pt-1 text-center text-truncate"
                  @click="openEbayLink(item)"
                >
                  {{ $t("parts.inventory.see_ebay") }}</a
                >
              </span>
            </td>

            <!---------------------------------------kTypes--------------------------------------->
            <td
              v-if="
                $store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'SuperAdmin' ||
                $store.getters.institutionExportEbayLeko
              "
              class="text-xs"
            >
              <span class="d-flex">
                <v-icon
                  v-if="
                    item &&
                    item.infoCar &&
                    item.infoCar.kTypes &&
                    item.infoCar.kTypes.length > 0
                  "
                  @click="checkKtypes(item)"
                  >mdi-check-circle-outline</v-icon
                >
                <v-icon v-else>mdi-cancel</v-icon>
                <p class="mb-0 ml-1">
                  {{
                    item.infoCar &&
                    item.infoCar.kTypes &&
                    item.infoCar.kTypes.length > 0
                      ? "(" + item.infoCar.kTypes.length + ")"
                      : ""
                  }}
                </p>
              </span>
            </td>
            <td>
              <div class="text-center">
                <v-rating
                  v-if="item.quality"
                  :model-value="item.quality.rating"
                  color="red"
                  background-color="red-darken-4"
                  readonly
                  :size="16"
                />
                <v-rating
                  v-else
                  :model-value="0"
                  color="red"
                  background-color="red-darken-4"
                  readonly
                  :size="16"
                />
              </div>
              <div class="text-center text-truncate pb-2">
                {{ formatDate(item.date_created) }}
              </div>
              <v-row justify="center" class="pt-2 mb-1">
                <v-btn
                  v-if="checkMovement(item)"
                  small
                  color="gray"
                  variant="outlined"
                  class="wrap"
                  @click="movement(item)"
                >
                  {{ $t("parts.status.returned") }} <br />
                  ({{ $t("parts.insert_parts.inserted_parts") }})
                </v-btn>
                <v-btn
                  v-else-if="item.status === 1"
                  small
                  color="success"
                  variant="outlined"
                  @click="movement(item)"
                >
                  {{ inventoryEnteredLabel(item) }}
                </v-btn>
                <v-btn
                  v-else-if="item.status === 2"
                  small
                  color="error"
                  variant="outlined"
                  @click="movement(item)"
                >
                  {{ $t("parts.status.returned") }}
                </v-btn>
                <v-btn
                  v-else-if="item.status === 2.1"
                  small
                  color="error"
                  variant="outlined"
                  @click="movement(item)"
                >
                  {{ $t("parts.status.returned_shop") }}
                </v-btn>
                <v-btn
                  v-else-if="item.status === 3"
                  small
                  color="info"
                  variant="outlined"
                  @click="movement(item)"
                >
                  {{ $t("parts.status.re_entered") }}
                </v-btn>
                <v-btn
                  v-else-if="item.status === 4"
                  small
                  color="info"
                  variant="outlined"
                  @click="movement(item)"
                >
                  {{ $t("parts.status.found") }}
                </v-btn>
              </v-row>
            </td>
          </tr>
          <tr v-if="parts && parts.length < 1">
            <td :colspan="headers.length" class="text-center pa-4">
              <v-alert type="info" border="left" color="red lighten-2" text>
                No data available.
              </v-alert>
            </td>
          </tr>
        </template>
        <template v-if="showPaginationLoader" #footer.prepend>
          <v-progress-linear
            v-if="loading"
            indeterminate
            color="red"
            height="3"
            class="inventory-pagination-loader"
          />
        </template>
      </v-data-table-server>

      <!-- Sticky horizontal scrollbar (synced with table scroll) -->
      <div
        v-show="true"
        ref="stickyHScroll"
        class="sticky-hscroll"
        :style="stickyHScrollStyle"
        @scroll.passive="onStickyHScroll"
      >
        <div :style="{ width: hScroll.contentWidth + 'px' }"></div>
      </div>
    </div>


    <v-dialog
      v-model="loadingPage"
      persistent
      fullscreen
      transition="fade-transition"
      content-class="d-flex align-center justify-center"
      overlay-color="black"
      overlay-opacity="0.3"
    >
      <v-card
        color="transparent"
        flat
        class="d-flex align-center justify-center"
      >
        <v-progress-circular color="#D32F2F" indeterminate size="100" />
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogDelete" width="auto">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert class="mb-0" variant="tonal" borer="bottom" type="error">
            {{ $t("delete_alert") }}
          </v-alert>
        </v-card-title>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogDelete = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn color="red" variant="outlined" @click="confirmDelete()">
            {{ $t("delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-btn
      v-show="showScrollTop"
      icon
      class="scroll-top-btn"
      color="#F44336"
      @click="scrollToTop"
    >
      <v-icon>mdi-arrow-up</v-icon>
    </v-btn>
  </div>
</template>

<script>
import moment from "moment";
import VueQrCode from "vue-qr-generator";
import axios from "axios";
import getRequest from "../../api/getRequest";
import deleteRequest from "../../api/deleteRequest";
import putRequest from "../../api/putRequest";
import EcoScoreBadge from "@/components/common/EcoScoreBadge.vue";
import { ecoScoreFor } from "@/utils/ecoScoreLookup";

let lastRequestId = 0;
export default {
  components: {
    "qr-code": VueQrCode,
    "eco-score-badge": EcoScoreBadge,
  },

  props: ["search"], // eslint-disable-line vue/require-prop-types
  data: () => ({
    loading: false,
    loadingPage: false,
    activeRow: null,
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["date_created"],
      sortBy: [{ key: "date_created", order: "desc" }],
      sortDesc: [true],
    },
    selected: [],
    totalItems: 0,
    parts: [],
    headers: [],
    headersAdmin: [
      {
        title: "",
        sortable: true,
        key: "_id",
      },
      {
        title: "",
        sortable: false,
        key: "actions",
      },
      {
        title: "",
        sortable: true,
        key: "shipment_id",
      },
      {
        title: "",
        sortable: true,
        key: "leko_group",
      },
      {
        title: "",
        sortable: true,
        key: "group",
      },
      {
        title: "",
        sortable: true,
        key: "infoCar.description.descriptionId.title.it",
      },
      {
        title: "",
        sortable: false,
        key: "manufacturer_code",
      },
      {
        title: "",
        sortable: false,
        key: "institution_id",
      },
      {
        title: "",
        sortable: false,
        key: "images",
      },
      {
        title: "",
        sortable: false,
        key: "isNewPart",
      },
      {
        title: "",
        sortable: false,
        key: "oemCode",
      },
      {
        title: "",
        sortable: true,
        key: "price",
      },
      {
        title: "",
        sortable: false,
        key: "ecoScore",
      },
      {
        title: "",
        sortable: true,
        key: "pra",
      },
      {
        title: "",
        sortable: false,
        key: "notes",
      },
      {
        title: "",
        sortable: true,
        key: "brand",
      },
      {
        title: "",
        sortable: true,
        key: "ebayCode",
      },
      {
        title: "",
        sortable: true,
        key: "eBayTitle",
      },
      {
        title: "",
        sortable: true,
        key: "Google",
      },
      {
        title: "",
        sortable: true,
        key: "ebay",
      },
      {
        title: "",
        sortable: true,
        key: "kTypes",
      },
      // {
      //   text: '',
      //   sortable: true,
      //   value: 'shelf_data.shelf.room_name',
      // },
      // {
      //   text: '',
      //   sortable: true,
      //   value: 'quality',
      // },
      {
        title: "",
        sortable: true,
        key: "status",
      },
      // {
      //   text: '',
      //   sortable: true,
      //   value: 'status',
      // },
    ],
    alert: {
      value: false,
      type: "success",
      message: "",
    },
    statusMap: {},
    // baseUrl: 'http://localhost:3000',
    // baseUrl: 'https://api.lekotech.com',
    idToDelete: null,
    kromedaToDelete: null,
    dialogDelete: false,
    editDialog: false,
    editedPrice: null,
    currentItem: null,
    activeEditId: null,
    mobileEditId: null,
    showScrollTop: false,
    hScroll: {
      visible: true,
      enabled: false,
      contentWidth: 0,
      left: 0,
      width: 0,
      bottom: 0,
    },
    _tableScrollEl: null,
    _syncingHScroll: false,
    _stickyInitTries: 0,
    _rafStickyRefresh: null,
    _moSticky: null,
    _roTableHost: null,
    _tableReady: false,
    _refreshScheduled: false,
  }),
  computed: {
    server() {
      return this.$serverUri;
    },
    stickyHScrollStyle() {
      return {
        left: `${this.hScroll.left}px`,
        width: `${this.hScroll.width}px`,
        bottom: `${this.hScroll.bottom}px`,
        opacity: this.hScroll.enabled ? "1" : "0.85",
        pointerEvents: this.hScroll.enabled ? "auto" : "none",
      };
    },
    searchObject() {
      return this.$props.search;
    },
    showPaginationLoader() {
      return this.pagination.itemsPerPage >= 10;
    },
    filteredItemsByStatusAndDate() {
      const desiredStatusValue = 2;
      const desiredDate = moment("2023-04-19");

      return this.items.filter((item) => {
        const itemDate = moment(item.date_created, "YYYYMMDD");
        return (
          item.status === desiredStatusValue &&
          itemDate.isSame(desiredDate, "day")
        );
      });
    },
  },
  watch: {
    selected: {
      handler(data) {
        if (data.length > 0) {
          this.$emit("selected", { value: true, items: data });
        } else {
          this.$emit("selected", { value: false, items: data });
        }
      },
      deep: true,
      immediate: true,
    },
    searchObject: {
      handler(value) {
        // Skip the initial prop; first load is driven by v-data-table-server
        // @update:options → updatePagination (avoids duplicate datatable calls).
        if (!this._tableReady) return;
        if (value.type === null) {
          this.getParts();
        } else if (value.type === "regularSearch") {
          this.regularSearch(value.data);
        } else if (value.type === "advancedSearch") {
          this.advancedSearch(value.data);
        }
      },
      deep: true,
    },
    parts(value) {
      this.$emit("items", value);
      this.$nextTick(() => this.refreshStickyHScroll());
    },
    "$i18n.locale"() {
      this.getLocale();
    },
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("scroll", this.onWindowScroll, { passive: true });
    window.addEventListener("resize", this.refreshStickyHScroll);
    this.$nextTick(() => this.initStickyHScroll());
    this.$nextTick(() => this.initStickyLayoutObservers());
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("scroll", this.onWindowScroll);
    window.removeEventListener("resize", this.refreshStickyHScroll);
    this.teardownStickyHScroll();
    this.teardownStickyLayoutObservers();
  },
  beforeMount() {
    // this.getParts();
    this.getLocale();
  },

  methods: {
    ecoScoreFor,
    keepPriceIfValid(value, current) {
      if (value === "" || value == null) return value;
      const n = Number(value);
      if (!Number.isFinite(n) || n > 10000) return current;
      return value;
    },
    initStickyLayoutObservers() {
      const host = this.$refs.tableHost;
      if (!host || !(host instanceof HTMLElement)) return;

      // Drawer open/close and other layout shifts can change widths
      // without triggering window resize. ResizeObserver catches that.
      if (!this._roTableHost && typeof ResizeObserver !== "undefined") {
        this._roTableHost = new ResizeObserver(() => {
          this.refreshStickyHScroll();
        });
        this._roTableHost.observe(host);
      }
    },
    teardownStickyLayoutObservers() {
      if (this._roTableHost) {
        this._roTableHost.disconnect();
        this._roTableHost = null;
      }
    },
    initStickyHScroll() {
      // Vuetify uses different wrapper classnames across versions.
      const host = this.$refs.tableHost;
      if (!host || !(host instanceof HTMLElement)) return;

      const wrapper =
        host.querySelector(".v-table__wrapper") ||
        host.querySelector(".v-data-table__wrapper") ||
        host.querySelector(".v-data-table__wrapper__scroll") ||
        host.querySelector("[data-v-table] .v-table__wrapper");

      if (!wrapper || !(wrapper instanceof HTMLElement)) {
        // The table DOM can appear after a tick (server table + pagination)
        if (this._stickyInitTries < 10) {
          this._stickyInitTries += 1;
          window.setTimeout(() => this.initStickyHScroll(), 200);
        }
        // Also observe DOM changes until wrapper appears
        if (!this._moSticky) {
          this._moSticky = new MutationObserver(() => {
            if (!this._tableScrollEl) this.initStickyHScroll();
          });
          this._moSticky.observe(host, { childList: true, subtree: true });
        }
        return;
      }

      this._tableScrollEl = wrapper;
      this._tableScrollEl.addEventListener("scroll", this.onTableHScroll, {
        passive: true,
      });

      // Once attached, observer is no longer needed
      if (this._moSticky) {
        this._moSticky.disconnect();
        this._moSticky = null;
      }

      this.refreshStickyHScroll();
    },
    teardownStickyHScroll() {
      if (this._tableScrollEl) {
        this._tableScrollEl.removeEventListener("scroll", this.onTableHScroll);
      }
      if (this._moSticky) {
        this._moSticky.disconnect();
        this._moSticky = null;
      }
      this._tableScrollEl = null;
      this._syncingHScroll = false;
    },
    refreshStickyHScroll() {
      const el = this._tableScrollEl;
      if (!el) {
        // try again once DOM is ready (pagination changes / rerenders)
        this.teardownStickyHScroll();
        this.$nextTick(() => this.initStickyHScroll());
        return;
      }

      // Position like an app footer: fixed at bottom, above existing v-footer
      const host = this.$refs.tableHost;
      if (host && host instanceof HTMLElement) {
        const rect = host.getBoundingClientRect();
        this.hScroll.left = Math.max(0, Math.round(rect.left));
        this.hScroll.width = Math.max(0, Math.round(rect.width));
      } else {
        this.hScroll.left = 0;
        this.hScroll.width = window.innerWidth || 0;
      }
      // If host is not measurable yet, force a usable width
      if (!this.hScroll.width || this.hScroll.width < 50) {
        this.hScroll.left = 0;
        this.hScroll.width = window.innerWidth || 0;
      }

      const footer = document.querySelector(".v-footer");
      const footerHeight =
        footer && footer instanceof HTMLElement
          ? Math.round(footer.getBoundingClientRect().height)
          : 0;
      // If footer isn't measurable yet, fall back to a typical app-footer height.
      // This ensures the bar stays above the footer on first render.
      this.hScroll.bottom = footerHeight > 0 ? footerHeight : 56;

      // Some Vuetify builds keep scrollWidth on the inner <table>
      const innerTable = el.querySelector("table");
      const scrollWidth = innerTable?.scrollWidth || el.scrollWidth || 0;
      const clientWidth = el.clientWidth || 0;
      const needs = scrollWidth > clientWidth + 1;

      this.hScroll.visible = true;
      this.hScroll.enabled = needs;
      // If Vuetify reports 0/incorrect widths, still create a usable scrollbar track
      const fallbackWidth =
        clientWidth > 0 ? clientWidth * 2 : (this.hScroll.width || 800) * 2;
      this.hScroll.contentWidth = Math.max(
        scrollWidth || 0,
        needs ? scrollWidth || fallbackWidth : clientWidth + 1,
        this.hScroll.width + 1,
      );

      // keep sticky bar aligned to current table scrollLeft
      if (needs) {
        const bar = this.$refs.stickyHScroll;
        if (bar && bar instanceof HTMLElement) {
          if (bar.scrollLeft !== el.scrollLeft) {
            bar.scrollLeft = el.scrollLeft;
          }
        }
      }
    },
    onTableHScroll() {
      if (this._syncingHScroll) return;
      const el = this._tableScrollEl;
      const bar = this.$refs.stickyHScroll;
      if (!el || !bar || !(bar instanceof HTMLElement)) return;

      this._syncingHScroll = true;
      bar.scrollLeft = el.scrollLeft;
      requestAnimationFrame(() => {
        this._syncingHScroll = false;
      });
    },
    onStickyHScroll() {
      if (this._syncingHScroll) return;
      const el = this._tableScrollEl;
      const bar = this.$refs.stickyHScroll;
      if (!el || !bar || !(bar instanceof HTMLElement)) return;

      this._syncingHScroll = true;
      el.scrollLeft = bar.scrollLeft;
      requestAnimationFrame(() => {
        this._syncingHScroll = false;
      });
    },
    onWindowScroll() {
      if (this._rafStickyRefresh) return;
      this._rafStickyRefresh = requestAnimationFrame(() => {
        this._rafStickyRefresh = null;
        this.refreshStickyHScroll();
      });
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    formatMobileShelfPosition(item) {
      const shelf = item?.shelf_data?.shelf;
      if (!shelf) return "";
      return `${shelf.room_name} - ${shelf.location_code}: ${shelf.x}, ${shelf.y}`;
    },
    openPriceAverages(code) {
      this.$emit("average-prices", code);
    },
    openAddEbay(item) {
      this.$emit("add-ebay", item);
    },
    openDeleteEbay(item) {
      this.$emit("delete-ebay", item);
    },
    openAddGoogle(item) {
      this.$emit("add-google", item);
    },
    openDeleteGoogle(item) {
      this.$emit("delete-google", item);
    },
    checkKtypes(item) {
      this.$emit("check-ktypes", item);
    },
    openEditTitleEbay(item) {
      this.$emit("edit-ebay-title", item);
    },
    getLocale() {
      const isGoogle = this.$store.getters.institutionExportGoogleLeko;
      const isEbay = this.$store.getters.institutionExportEbayLeko;

      const allHeaders = [
        { title: this.$t("parts.table.qr_code"), key: "_id", sortable: true },
        {
          title: this.$t("parts.table.actions"),
          key: "actions",
          sortable: false,
        },
        {
          title: this.$t("parts.table.shipment"),
          key: "shipment_id",
          sortable: true,
        },
        {
          title: this.$t("parts.table.leko_group"),
          key: "leko_group",
          sortable: true,
        },
        { title: this.$t("parts.table.group"), key: "group", sortable: true },
        {
          title: this.$t("parts.table.title"),
          key: "infoCar.description.descriptionId.title.it",
          sortable: true,
        },
        {
          title: this.$t("parts.table.manufacturer_code"),
          key: "manufacturer_code",
          sortable: false,
        },
        {
          title: this.$t("parts.table.images"),
          key: "images",
          sortable: false,
        },
        {
          title: this.$t("parts.table.new"),
          key: "isNewPart",
          sortable: false,
        },
        {
          title: this.$t("parts.table.oem_code"),
          key: "oemCode",
          sortable: false,
        },
        { title: this.$t("parts.table.price"), key: "price", sortable: true },
        { title: this.$t("ecoScore.column"), key: "ecoScore", sortable: false },
        { title: this.$t("parts.table.pra"), key: "pra", sortable: true },
        { title: this.$t("parts.table.notes"), key: "notes", sortable: false },
        { title: this.$t("parts.table.brand"), key: "brand", sortable: true },

        // CONDITIONAL:
        {
          title: this.$t("parts.table.ebay_code"),
          key: "ebayCode",
          sortable: true,
          condition: isEbay,
        },
        {
          title: this.$t("parts.table.ebay_title"),
          key: "eBayTitle",
          sortable: true,
          condition: isEbay,
        },

        {
          title: this.$t("parts.table.google"),
          key: "Google",
          sortable: true,
          condition: isGoogle,
        },
        {
          title: this.$t("parts.table.ebay"),
          key: "eBay",
          sortable: true,
          condition: isEbay,
        },

        // kTypes and date_created should appear only ONCE, after google/ebay
        {
          title: this.$t("parts.table.kTypes"),
          key: "kTypes",
          sortable: true,
          condition: isEbay,
        },
        {
          title: this.$t("parts.table.date_created"),
          key: "date_created",
          sortable: true,
        },
      ];

      // Filter out columns with a `condition` that evaluates to false or undefined
      this.headers = allHeaders.filter((header) => {
        return header.condition === undefined || header.condition === true;
      });
      if (
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
      ) {
        this.headersAdmin[0].title = this.$t("parts.table.qr_code");
        this.headersAdmin[1].title = this.$t("parts.table.actions");
        this.headersAdmin[2].title = this.$t("parts.table.shipment");
        this.headersAdmin[3].title = this.$t("parts.table.leko_group");
        this.headersAdmin[4].title = this.$t("parts.table.group");
        this.headersAdmin[5].title = this.$t("parts.table.title");
        this.headersAdmin[6].title = this.$t("parts.table.manufacturer_code");
        this.headersAdmin[7].title = this.$t("parts.table.institution");
        this.headersAdmin[8].title = this.$t("parts.table.images");
        this.headersAdmin[9].title = this.$t("parts.table.new");
        this.headersAdmin[10].title = this.$t("parts.table.oem_code");
        this.headersAdmin[11].title = this.$t("parts.table.price");
        this.headersAdmin[12].title = this.$t("ecoScore.column");
        this.headersAdmin[13].title = this.$t("parts.table.pra");
        this.headersAdmin[14].title = this.$t("parts.table.notes");
        this.headersAdmin[15].title = this.$t("parts.table.brand");
        this.headersAdmin[16].title = this.$t("parts.table.ebay_code");
        this.headersAdmin[17].title = this.$t("parts.table.ebay_title");
        this.headersAdmin[18].title = this.$t("parts.table.google");
        this.headersAdmin[19].title = this.$t("parts.table.ebay");
        this.headersAdmin[20].title = this.$t("parts.table.kTypes");
        this.headersAdmin[21].title = this.$t("parts.table.date_created");
        // this.headersAdmin[11].text = this.$t('parts.table.location');
        // this.headersAdmin[14].text = this.$t('parts.table.quality');
        // this.headersAdmin[21].text = this.$t('parts.table.date_created');
        // this.headersAdmin[14].text = this.$t('parts.table.status');
      }
    },
    async getParts() {
      const requestId = ++lastRequestId;
      this.loading = true;

      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      try {
        const response = await getRequest(
          "/part/inventory/datatable",
          `institution_id=${this.$store.getters.institutionId}&role=${
            this.$store.getters.role
          }&limit=${this.pagination.itemsPerPage}&page=${
            this.pagination.page
          }&sort_by=${sort.key}&descending=${sort.order === "desc"}`,
        );
        if (requestId !== lastRequestId) return;
        // this.$scrollToTop();
        this.totalItems = response.count;
        this.parts = response.parts || [];
        this.loading = false;
      } catch {
        this.loading = false;
      } finally {
        // this.loading = false;
      }
    },
    groupEngineSearchParam(data) {
      const general = data && (data.type === "other" || data.type === "multipleTitlesOther");
      return general ? "&include_engine=1" : "";
    },
    regularSearch(data) {
      const requestId = ++lastRequestId;
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      getRequest(
        "/part/inventory/search/regular",
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${
          sort.order === "desc"
        }&search_field=${data.searchField}&title_code=${
          data.titleCode
        }&title_codes=${data.titleCodes}&type=${data.type}&note=${
          data.note
        }&code=${data.code}&language=${this.$store.getters.language}${this.groupEngineSearchParam(data)}`,
      )
        .then((response) => {
          // this.$scrollToTop();
          if (requestId !== lastRequestId) return;
          this.totalItems = response.count;
          this.parts = response.parts;
          if (response.count === 0) {
            this.checkVehiclesNew(null, null, response.rr || null);
          }
          this.loading = false;
        })

        .catch(() => {
          this.loading = false;
        })
        .finally(() => {
          // this.loading = false;
        });
    },
    advancedSearch(data) {
      const requestId = ++lastRequestId;
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      getRequest(
        "/part/inventory/search/advanced",
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${
          sort.order === "desc"
        }&search_field=${data.searchField}&language=${
          this.$store.getters.language
        }&title_code=${data.titleCode}&title_codes=${data.titleCodes}&type=${
          data.type
        }&brand=${data.advancedSearchFields.brand}&positionCode=${
          data.advancedSearchFields.positionCode
        }&position=${data.advancedSearchFields.position}&eBayStatus=${
          data.advancedSearchFields.eBayStatus
        }&isNewPart=${data.advancedSearchFields.isNewPart}&no_shipping_cost=${
          data.advancedSearchFields.no_shipping_cost
        }&invalid_category=${
          data.advancedSearchFields.invalid_category
        }&model=${data.advancedSearchFields.model}&version=${
          data.advancedSearchFields.version
        }&institution=${data.advancedSearchFields.institution}&shipment=${
          data.advancedSearchFields.shipment
        }&status=${data.advancedSearchFields.status}&quality=${
          data.advancedSearchFields.quality
        }&price=${data.advancedSearchFields.price}&start_date=${
          data.advancedSearchFields.startDate
        }&end_date=${data.advancedSearchFields.endDate}&no_applicability=${
          data.advancedSearchFields.noApplicability
        }&no_title_no_oem=${
          data.advancedSearchFields.noTitleNoOem
        }&no_notes=${data.advancedSearchFields.noNotes}&custom_applicability=${
          data.advancedSearchFields.customApplicability === true
        }&label_origin=${data.advancedSearchFields.labelOrigin || ""}&position_mismatch=${
          data.advancedSearchFields.positionMismatch
        }&note=${
          data.note
        }&code=${data.code}&repository=${
          data.advancedSearchFields.repository
        }&room=${data.advancedSearchFields.room}&location=${
          data.advancedSearchFields.location
        }${this.groupEngineSearchParam(data)}`,
      )
        .then((response) => {
          // this.$scrollToTop();
          if (requestId !== lastRequestId) return;
          this.totalItems = response.count;
          this.parts = response.parts;
          // if the brand and model are specified but there are no results, check if there are any
          // inserted vehicles with the specified details.
          if (response.count === 0) {
            this.checkVehiclesNew(
              data.advancedSearchFields.brand,
              data.advancedSearchFields.model,
              response.rr || null,
              data.titleCode,
              data.advancedSearchFields.version,
            );
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        })
        .finally(() => {
          // this.loading = false;
        });
    },
    deletePart(id, kromeda) {
      this.idToDelete = id;
      this.kromedaToDelete = kromeda;
      this.dialogDelete = true;
    },
    confirmDelete() {
      this.loading = true;
      deleteRequest(
        `/part/inventory/${this.idToDelete}`,
        this.kromedaToDelete
          ? `kromeda_id=${this.kromedaToDelete._id}
            &user_id=${
              this.$store.getters.userId
            }&institution_id=${this.$store.getters.institutionId}`
          : `user_id=${
              this.$store.getters.userId
            }&institution_id=${this.$store.getters.institutionId}`,
      )
        .then((response) => {
          if (response) {
            this.idToDelete = null;
            this.kromedaToDelete = null;
            this.dialogDelete = false;
            this.$emit("delete");
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    checkVehicles(brand, model, rr) {
      if (brand && model) {
        getRequest(
          "/car/count",
          `brand=${brand}&model=${model}&institution_id=${this.$store.getters.institutionId}`,
        )
          .then((response) => {
            if (response.count > 0) this.$emit("show-cars", { brand, model });
          })
          .catch(() => {})
          .finally(() => {});
      }
    },

    checkVehiclesNew(
      brand,
      model,
      rr = null,
      titleCode = null,
      version = null,
    ) {
      // Case 1: No brand/model, but rr is true → directly open modal with rr flag
      if ((!brand || !model) && rr) {
        this.$emit("show-cars", { rr });
        return;
      }

      const promises = [];
      let carCountIndex = null;
      let partCodeExistsIndex = null;

      if (brand && model) {
        carCountIndex = promises.length;
        promises.push(
          getRequest(
            "/car/count",
            `brand=${brand}&model=${model}&institution_id=${this.$store.getters.institutionId}`,
          ),
        );
      }

      if (brand && model && version && titleCode) {
        partCodeExistsIndex = promises.length;
        promises.push(
          getRequest(
            "/car/part-code-exists",
            `brand=${brand}&model=${model}&version=${version}&institution_id=${this.$store.getters.institutionId}&title_code=${titleCode}`,
          ),
        );
      }

      if (!promises.length) {
        if (rr) this.$emit("show-cars", { rr });
        return;
      }

      Promise.all(promises)
        .then((results) => {
          const emitData = {};

          if (carCountIndex !== null && results[carCountIndex].count > 0) {
            emitData.hasCars = true;
            emitData.brand = brand;
            emitData.model = model;
          }

          if (
            partCodeExistsIndex !== null &&
            results[partCodeExistsIndex].count > 0
          ) {
            emitData.checkVehicle = true;
            emitData.brand = brand;
            emitData.model = model;
            emitData.version = version;
            emitData.titleCode = titleCode;
          }

          if (rr) emitData.rr = rr;

          if (emitData.hasCars || emitData.checkVehicle || emitData.rr) {
            this.$emit("show-cars", emitData);
          }
        })
        .catch(() => {
          if (rr) this.$emit("show-cars", { rr });
        });
    },
    edit(data) {
      this.$emit("edit", data);
    },
    share(data) {
      this.$emit("share", data);
    },
    actions(data) {
      this.$emit("actions", { part: data });
    },
    details(data) {
      this.$emit("details", data);
    },
    movement(data) {
      this.$emit("movement", data);
    },
    openQrDialog(data) {
      this.$emit("qr", data);
    },
    showImages(images) {
      this.$emit("images", { type: "string", images });
    },
    viewCar(carId) {
      this.$emit("view-car", { carId });
    },
    updatePagination(options) {
      const next = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length
            ? options.groupBy
            : ["date_created"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : [{ key: "date_created", order: "desc" }],
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };

      const prev = this.pagination;
      const sameOptions =
        this._tableReady &&
        prev &&
        next.page === prev.page &&
        next.itemsPerPage === prev.itemsPerPage &&
        JSON.stringify(next.sortBy) === JSON.stringify(prev.sortBy) &&
        JSON.stringify(next.sortDesc) === JSON.stringify(prev.sortDesc);

      this.pagination = next;
      this.activeRow = null;
      this._tableReady = true;

      // Skip no-op duplicates from v-model:options + @update:options.
      if (sameOptions) return;

      // Coalesce multiple update:options emissions in the same tick (Vuetify
      // often emits more than once on mount) into a single datatable request.
      if (this._refreshScheduled) return;
      this._refreshScheduled = true;
      this.$nextTick(() => {
        this._refreshScheduled = false;
        this.refreshParts();
      });
    },
    getPosition(code) {
      switch (code) {
        case "S001":
          return this.$t("left");
        case "S002":
          return this.$t("right");
        default:
          return "";
      }
    },
    openEditDialog(item) {
      this.currentItem = item;
      this.editedPrice = item.price;
      this.editDialog = true;
    },
    toggleMenu(item) {
      // if same item clicked again, close it
      if (this.activeEditId === item._id) {
        this.activeEditId = null;
        return;
      }
      // open menu for this item
      this.activeEditId = item._id;
      this.editedPrice = item.price;
      this.currentItem = item;
    },
    savePriceDialog() {
      if (this.currentItem) {
        this.currentItem.price = this.editedPrice;
        this.savePrice(this.currentItem, this.editedPrice);
      }
      this.editDialog = false;
    },

    savePrice(item, price) {
      this.loadingPage = true;
      putRequest("/part/price", `id=${item._id}`, { price })
        .then((response) => {
          if (
            this.$store.getters.institutionExportEbayLeko &&
            item.eBayStatus
          ) {
            if (
              response.data &&
              response.data.ebayError &&
              response.data.ebayError != null
            ) {
              this.$emit("show-error-modal", response.data.ebayError);
            }
          }
          this.refreshParts();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingPage = false;
          this.editDialog = false;
          this.activeEditId = null;
        });
    },
    cancelPrice() {
      this.editDialog = false;
      this.activeEditId = null;
      this.editedPrice = null;
      this.refreshParts();
    },

    refreshParts() {
      if (this.searchObject.type === null) {
        this.getParts();
      } else if (this.searchObject.type === "regularSearch") {
        this.regularSearch(this.searchObject.data);
      } else if (this.searchObject.type === "advancedSearch") {
        this.advancedSearch(this.searchObject.data);
      } else {
        //
      }
    },
    showEngineCodeNotesMatch(item) {
      if (!this.searchObject?.data || this.searchObject.data.type !== "engineCode") {
        return false;
      }
      const source = item.engineCodeMatchSource;
      return source === "notes" || source === "both";
    },
    showNotes(notes) {
      this.$emit("show-notes", notes);
    },
    showRefusalReason(reason) {
      this.$emit("show-refusal-reason", reason);
    },
    clearSelected() {
      // invoked from the 'Inventory' view
      this.selected = [];
    },
    openEbayLink(item) {
      this.$emit("see-ebay-items", item);
    },
    openGoogleLink(item) {
      //TBD
      // let url = ``
      // window.open(url,'_blank');
    },
    generateTitle(item) {
      if (!item) return "";
      if (item.eBayTitle) return item.eBayTitle;

      const language = this.$store.getters.language;
      const manufacturerCode =
        item.infoCar && item.infoCar.oemCode ? item.infoCar.oemCode : "";
      const descriptionTitle =
        item.infoCar &&
        item.infoCar.description &&
        item.infoCar.description.descriptionId &&
        item.infoCar.description.descriptionId.title
          ? item.infoCar.description.descriptionId.title[language]
          : "";
      const position =
        item.infoCar &&
        item.infoCar.description &&
        item.infoCar.description.positionCode
          ? this.getPosition(item.infoCar.description.positionCode)
          : "";
      const qr = item.old_id.slice(-6);
      return `${manufacturerCode} ${descriptionTitle} ${position} ${qr}`.trim();
    },
    checkMovement(item) {
      return item.movement_history?.some(
        (movement) =>
          movement.movement_data &&
          movement.movement_data.source === "Insert Parts" &&
          movement.movement_data.destination === "Sales",
      );
    },
    isRefusedEntered(item) {
      if (item?.refusal_reason && String(item.refusal_reason).trim()) return true;
      return item.movement_history?.some(
        (movement) => Number(movement.status) === 1.3,
      );
    },
    inventoryEnteredLabel(item) {
      return this.isRefusedEntered(item)
        ? this.$t("parts.status.entered_refused")
        : this.$t("parts.status.entered");
    },
    toggleSelection(item) {
      const index = this.selected.findIndex((s) => s._id === item._id);
      if (index === -1) {
        this.selected.push(item);
      } else {
        this.selected.splice(index, 1);
      }
    },
    handleScroll() {
      this.showScrollTop = window.scrollY > 300; // show button after scrolling down 300px
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    positionAlertItems(item) {
      const mismatches = item?.alerts?.mismatches;
      if (!Array.isArray(mismatches)) return [];
      return mismatches.filter((a) => a && a.field === "position");
    },
    getAlertIcon(type) {
      switch (type) {
        case "info":
          return "mdi-information";
        case "warning":
          return "mdi-alert-circle";
        case "error":
          return "mdi-close-octagon";
        default:
          return "";
      }
    },
    getAlertColor(type) {
      switch (type) {
        case "info":
          return "blue";
        case "warning":
          return "orange";
        case "error":
          return "red";
        default:
          return "";
      }
    },
  },
};
</script>
<style scoped>
.wrap {
  white-space: pre-line !important;
  text-align: center;
  height: 42px;
}
.v-data-table-header th {
  white-space: nowrap !important;
}
.scroll-top-btn {
  position: fixed;
  bottom: 60px;
  right: 15px;
  z-index: 9999;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}

.inventory-table-host {
  position: relative;
}

.inventory-table-host :deep(.v-data-table-footer .inventory-pagination-loader) {
  flex: 0 0 100%;
  width: 100%;
  margin-bottom: 4px;
}

.inventory-table-host :deep(.v-table__wrapper),
.inventory-table-host :deep(.v-data-table__wrapper),
.inventory-table-host :deep(.v-data-table__wrapper__scroll) {
  /* Keep horizontal scrolling, but hide the native scrollbar
     so users use the sticky bar above the footer. */
  scrollbar-width: none; /* Firefox */
}
.inventory-table-host :deep(.v-table__wrapper::-webkit-scrollbar),
.inventory-table-host :deep(.v-data-table__wrapper::-webkit-scrollbar),
.inventory-table-host :deep(.v-data-table__wrapper__scroll::-webkit-scrollbar) {
  height: 0px; /* Chromium/Safari */
}

.sticky-hscroll {
  position: fixed;
  z-index: 999; /* above v-footer */
  overflow-x: scroll; /* keep a dedicated scrollbar area */
  overflow-y: hidden;
  height: 18px; /* more visible on macOS overlay scrollbars */
  scrollbar-gutter: stable both-edges;
  background: rgb(var(--v-theme-surface, 255 255 255));
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.12);
}

.sticky-hscroll > div {
  height: 1px; /* just to create scrollable width */
}

/* ── Mobile / Desktop visibility toggle ── */
@media (max-width: 959.98px) {
  .inventory-table-host :deep(.v-table__wrapper) {
    display: none !important;
  }
  .inventory-table-host :deep(.sticky-hscroll) {
    display: none !important;
  }
  .inventory-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .inventory-mobile-list {
    display: none !important;
  }
}

/* ── Mobile card styles ── */
.inventory-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.inventory-mobile-card__price {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
}
.inventory-mobile-card--moved {
  background-color: #afabab !important;
}
.inventory-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.inventory-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 8px;
  gap: 8px;
}
.inventory-mobile-card__media {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.inventory-mobile-card__image-btn {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.inventory-mobile-card__img {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  background: #212121;
  display: block;
}
.inventory-mobile-card__qr-btn {
  min-width: 72px;
  margin-top: 2px;
  padding: 4px 8px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  color: #424242;
  cursor: pointer;
  text-align: center;
}
.inventory-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.inventory-mobile-card__location-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  line-height: 1.35;
}
.inventory-mobile-card__location-row + .inventory-mobile-card__location-row {
  margin-top: 2px;
}
.inventory-mobile-card__info-top {
  padding-right: 88px; /* reserve space for the absolute-positioned price button */
}
.inventory-mobile-card__institution {
  max-width: 100%;
}
.inventory-mobile-card__institution :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
.inventory-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 2px 4px;
}
</style>
